import React, { useState, useContext, useEffect, useRef } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { toast } from './toast';
import UserContext from './UserContext';

const FrameProcessor = ({ submissionId, userId, videoAngle, timestampImages, onSuccess, disabled, rotation, skipCrop }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { auth } = useContext(UserContext);
  const [done, setDone] = useState(false);
  const [progressText, setProgressText] = useState("");
  const skipNextReset = useRef(false);

  // Reset "done" state if images change (but not when the change came from a successful process)
  useEffect(() => {
    if (skipNextReset.current) {
      skipNextReset.current = false;
      return;
    }
    setDone(false);
  }, [timestampImages]);

  const handleProcess = async () => {
    try {
      setIsProcessing(true);
      setProgressText("Preparing...");
      const idToken = await auth.currentUser.getIdToken();
      
      const imagesToProcess = timestampImages[videoAngle];
      
      // Correctly count all frames, including nested ones for back view
      let count = 0;
      Object.values(imagesToProcess).forEach(val => {
        if (typeof val === 'object' && val !== null) {
          count += Object.values(val).filter(v => v !== null && v !== "").length;
        } else if (val !== null && val !== "") {
          count++;
        }
      });
      
      setProgressText(`Uploading ${count} frames...`);

      const response = await fetch('https://forma-pose-k6x63vv6tq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          submissionId,
          userId,
          videoAngle,
          images: imagesToProcess,
          rotation: rotation || 0,
          skipCrop: skipCrop || false,
        })
      });

      if (!response.ok) {
        let errorMsg = 'Failed to process frames';
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          // Fallback if not JSON
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      toast.success("AI Processing Complete");
      
      if (result.images) {
        skipNextReset.current = true;
        onSuccess(result.images, result.metrics || null, result.landmarks || null, result.timing || null);
        setDone(true);
      }
    } catch (error) {
      console.error('Error processing frames:', error);
      toast.error(error.message || "AI Processing Failed. Please try again.");
    } finally {
      setIsProcessing(false);
      setProgressText("");
    }
  };

  return (
    <Button 
      variant="outlined"
      color={done ? "success" : "primary"}
      onClick={handleProcess}
      disabled={disabled || isProcessing}
      startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {isProcessing ? (progressText || "Processing...") : done ? "Re-process Frames" : "Process Frames"}
    </Button>
  );
};

export default FrameProcessor;
